import React from "react";

import { useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { ShortTermDisabilityContextProvider } from "EmployeeCoverage/Components/ShortTermDisability/shortTermDisability.context";
import { ShortTermDisabilityEnrollmentModals } from "EmployeeCoverage/Components/ShortTermDisability/shortTermDisability.modals";
import { ShortTermDisabilityMultiplePlans } from "EmployeeCoverage/Components/ShortTermDisability/shortTermDisability.multiplePlans";
import { ShortTermDisabilityPlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { ShortTermDisabilitySinglePlan } from "./shortTermDisability.singlePlan";

type Data =
  | {
      isLoading: true;
      plans: any;
      planCosts: any;
    }
  | {
      isLoading: false;
      plans: Array<ShortTermDisabilityPlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const { isLoading: isLoadingPlans, data: { plans = [] } = {} } = useGetPlansQuery(
    {
      id,
      type: "short_term_disability",
    },
    { skip: !id },
  );
  const { isLoading: isLoadingCosts, data: { planCosts } = {} } = useGetPlanCostsQuery(
    {
      id,
      type: "short_term_disability",
    },
    { skip: plans.length === 0 },
  );

  return {
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as short term disability plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, plans = [], planCosts = [] } = useData();
  if (isLoading) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <ShortTermDisabilitySinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <ShortTermDisabilityMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const ShortTermDisabilityPage = () => (
  <ShortTermDisabilityContextProvider>
    <StateManager />
    <ShortTermDisabilityEnrollmentModals />
  </ShortTermDisabilityContextProvider>
);
